<script setup lang="ts">
/**
 * A painted toggle. The native checkbox stays — it carries the label
 * association, the keyboard behaviour and the form semantics, and replacing
 * those by hand is how checkboxes get broken. The track drawn in its place is
 * a dark steel groove with a plain brass knob; on, the groove turns muted sea
 * green. No glyph on the knob: the position says it (2026-10-14, the painted
 * cross and tick read as cartoon).
 */

defineProps<{ disabled?: boolean }>();
const model = defineModel<boolean>({ required: true });
</script>

<template>
  <label class="check" :class="{ disabled }">
    <input v-model="model" type="checkbox" :disabled="disabled" v-bind="$attrs" />
    <span class="track" aria-hidden="true">
      <span class="knob"></span>
    </span>
    <span class="text"><slot /></span>
  </label>
</template>

<script lang="ts">
export default { inheritAttrs: false };
</script>

<style scoped>
.check {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  font-family: var(--font-game-body);
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.35;
  color: inherit;
  cursor: pointer;
  touch-action: manipulation;
}

.check.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

input {
  position: absolute;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: inherit;
}

.track {
  --w: 48px;
  --h: 26px;
  --pad: 3px;
  position: relative;
  flex: none;
  width: var(--w);
  height: var(--h);
  border-radius: 999px;
  background: linear-gradient(180deg, #10171f, #1e2a36);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(185, 138, 58, 0.55);
  transition: background var(--dur) var(--ease);
}

.knob {
  position: absolute;
  top: var(--pad);
  left: var(--pad);
  width: calc(var(--h) - 2 * var(--pad));
  height: calc(var(--h) - 2 * var(--pad));
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #e9d29a, #b88e44 60%, #6f5220);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  transition: transform var(--dur) var(--ease);
}

input:checked ~ .track {
  background: linear-gradient(180deg, #1d4a36, #2f6a4f);
}

input:checked ~ .track .knob {
  transform: translateX(calc(var(--w) - var(--h)));
}

input:focus-visible ~ .track {
  outline: 3px solid #fff;
  outline-offset: 3px;
}

.text {
  min-width: 0;
}

@media (prefers-reduced-motion: reduce) {
  .track,
  .knob {
    transition: none;
  }
}
</style>
